import { useState, useEffect, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import {
  Heart,
  MessageCircle,
  Repeat2,
  Bookmark,
  Send,
  ImageIcon,
  X,
} from 'lucide-react'
import { api, showLoginModal } from '@/lib/api'
import { useAuth } from '@/context/AuthContext'
import { formatName, resolveImg, timeAgo } from '@/lib/helpers'
import Avatar from '@/components/ui/Avatar'


export default function Feed() {
  const { user } = useAuth()
  const [posts, setPosts] = useState<any[]>([])
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(true)
  const [selectedPost, setSelectedPost] = useState<any>(null)
  const pageRef = useRef(1)
  const loadingRef = useRef(false)
  const observerInstance = useRef<IntersectionObserver | null>(null)

  const loadPosts = useCallback(async (pageNum: number, append = false) => {
    try {
      setLoading(true)
      const res = await api.posts.getFeed(pageNum)
      const data = res?.data || []
      setPosts((prev) => (append ? [...prev, ...data] : data))
      setHasMore(pageNum < (res?.pagination?.pages || 1))
    } catch {
      // ignore
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    pageRef.current = 1
    loadPosts(1)
  }, [loadPosts])

  // Infinite scroll — callback ref ensures observer connects when sentinel mounts
  const sentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (observerInstance.current) {
        observerInstance.current.disconnect()
        observerInstance.current = null
      }
      if (!node) return
      observerInstance.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && !loadingRef.current) {
            const next = pageRef.current + 1
            pageRef.current = next
            loadingRef.current = true
            loadPosts(next, true).then(() => { loadingRef.current = false })
          }
        },
        { threshold: 0.1 }
      )
      observerInstance.current.observe(node)
    },
    [loadPosts]
  )

  const openPost = (post: any) => {
    setSelectedPost(post)
    document.body.style.overflow = 'hidden'
  }

  const closeModal = () => {
    setSelectedPost(null)
    document.body.style.overflow = ''
  }

  // Sync modal post comments back to feed when modal closes
  const onModalCommentsUpdate = (postId: string, comments: any[]) => {
    setPosts((prev) =>
      prev.map((p) => (p._id === postId ? { ...p, comments } : p))
    )
  }

  return (
    <div className="max-w-[640px] mx-auto px-4 py-6 pb-16">
      {/* Create post */}
      {user && <CreatePost onCreated={() => loadPosts(1)} />}

      {/* Posts feed */}
      <div className="flex flex-col gap-4 mt-4">
        {posts.map((post) => (
          <PostCard
            key={post._id}
            post={post}
            currentUserId={user?._id}
            onOpenDetail={() => openPost(post)}
          />
        ))}
      </div>

      {loading && posts.length === 0 && (
        <div className="text-center text-gray-400 py-20">Ачаалж байна...</div>
      )}

      {!loading && posts.length === 0 && (
        <div className="text-center py-20">
          <p className="text-gray-400 text-lg">Нийтлэл одоогоор алга</p>
        </div>
      )}

      {/* Infinite scroll sentinel */}
      {hasMore && posts.length > 0 && (
        <div ref={sentinelRef} className="flex justify-center py-6">
          <div className="size-6 border-2 border-gray-300 border-t-primary-500 rounded-full animate-spin" />
        </div>
      )}

      {/* Post Detail Modal */}
      {selectedPost && (
        <PostDetailModal
          post={selectedPost}
          currentUserId={user?._id}
          currentUser={user}
          onClose={closeModal}
          onCommentsUpdate={onModalCommentsUpdate}
        />
      )}
    </div>
  )
}

/* ── Create Post ────────────────────────── */
function CreatePost({ onCreated }: { onCreated: () => void }) {
  const { user } = useAuth()
  const [content, setContent] = useState('')
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState('')
  const [uploading, setUploading] = useState(false)
  const [sending, setSending] = useState(false)
  const [success, setSuccess] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setImageFile(file)
    setImagePreview(URL.createObjectURL(file))
  }

  const removeImage = () => {
    setImageFile(null)
    setImagePreview('')
    if (fileRef.current) fileRef.current.value = ''
  }

  const handleSubmit = async () => {
    if ((!content.trim() && !imageFile) || sending) return
    setSending(true)
    try {
      let imageUrl: string | undefined
      if (imageFile) {
        setUploading(true)
        const res = await api.upload.image(imageFile)
        imageUrl = res.url
        setUploading(false)
      }
      await api.posts.create({ content: content.trim(), imageUrl })
      setContent('')
      removeImage()
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
      onCreated()
    } catch {
      // ignore
    } finally {
      setSending(false)
      setUploading(false)
    }
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Success toast */}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 text-sm font-medium rounded-xl px-4 py-3 flex items-center gap-2 animate-in fade-in slide-in-from-top-2 duration-200">
          <svg className="size-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          Нийтлэл амжилттай нийтлэгдлээ!
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-2xl p-4">
        <div className="flex gap-3">
          <Avatar
            src={resolveImg(user?.avatar)}
            alt={formatName(user?.fullName) || ''}
            size="sm"
            className="shrink-0"
          />
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Юу бодож байна?"
            rows={2}
            className="flex-1 resize-none outline-none text-sm text-gray-900 placeholder:text-gray-400 bg-transparent"
          />
        </div>

        {/* Image preview */}
        {imagePreview && (
          <div className="relative mt-3 mx-10">
            <img
              src={imagePreview}
              alt="Preview"
              className="w-full rounded-xl object-cover max-h-[200px]"
            />
            <button
              onClick={removeImage}
              className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1 hover:bg-black/80 transition"
            >
              <X className="size-4" />
            </button>
          </div>
        )}

        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={handleImageSelect}
          />
          <button
            onClick={() => fileRef.current?.click()}
            className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 transition"
          >
            <ImageIcon className="size-4" />
            Зураг
          </button>
          <button
            onClick={handleSubmit}
            disabled={(!content.trim() && !imageFile) || sending}
            className="bg-primary-500 text-white rounded-full px-4 py-1.5 text-sm font-semibold hover:bg-primary-600 transition disabled:opacity-50 flex items-center gap-1.5"
          >
            <Send className="size-3.5" />
            {uploading ? 'Зураг хуулж байна...' : sending ? 'Илгээж байна...' : 'Нийтлэх'}
          </button>
        </div>
      </div>
    </div>
  )
}

/* ── Post Card ──────────────────────────── */
function PostCard({
  post,
  currentUserId,
  onOpenDetail,
}: {
  post: any
  currentUserId?: string
  onOpenDetail: () => void
}) {
  const [liked, setLiked] = useState(
    Array.isArray(post.likes) && currentUserId
      ? post.likes.some((id: any) => (id?._id || id)?.toString() === currentUserId)
      : false
  )
  const [likeCount, setLikeCount] = useState(
    Array.isArray(post.likes) ? post.likes.length : post.likeCount || 0
  )
  const [bookmarked, setBookmarked] = useState(
    Array.isArray(post.bookmarks) && currentUserId
      ? post.bookmarks.some((id: any) => (id?._id || id)?.toString() === currentUserId)
      : false
  )
  const [repostCount, setRepostCount] = useState(
    Array.isArray(post.reposts) ? post.reposts.length : post.repostCount || 0
  )
  const commentCount = Array.isArray(post.comments) ? post.comments.length : 0

  const author = post.userId || {}
  const authorName = formatName(author.fullName) || 'Хэрэглэгч'
  const authorAvatar = resolveImg(author.avatar)
  const image = resolveImg(post.imageUrl)
  const { user } = useAuth()
  const handleLike = async () => {
    if(!user) {showLoginModal(); return }
    try {
      const res = await api.posts.toggleLike(post._id)
      // Backend returns { liked: boolean, likes: number }
      setLiked(res.liked)
      setLikeCount(res.likes)
    } catch {
      // 401 (нэвтрээгүй) бол api.ts автомат login modal нээнэ — UI өөрчлөгдөхгүй
    }
  }

  const handleRepost = async () => {
    if( !user ) {showLoginModal(); return}
    try {
      const res = await api.posts.toggleRepost(post._id)
      setRepostCount((c: number) => (res.reposted ? c + 1 : c - 1))
    } catch {
      // 401 → login modal
    }
  }

  const handleBookmark = async () => {
    if( !user ) {showLoginModal(); return}
    try {
      const res = await api.posts.toggleBookmark(post._id)
      setBookmarked(res.bookmarked)
    } catch {
      // 401 → login modal
    }
  }

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
      {/* Author header */}
      <div className="flex items-center gap-3 p-4 pb-0">
        <Link to={`/teachers/${author._id || 'me'}`}>
          <Avatar src={authorAvatar} alt={authorName} size="sm" />
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Link
              to={`/teachers/${author._id || 'me'}`}
              className="font-semibold text-sm text-gray-900 hover:text-primary-500 transition"
            >
              {authorName}
            </Link>
            {post.isMemberOnly && (
              <span className="text-[11px] font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                Зөвхөн гишүүдэд
              </span>
            )}
          </div>
          <p className="text-xs text-gray-400">{timeAgo(post.createdAt)}</p>
        </div>
      </div>

      {/* Content — click opens modal */}
      <button
        onClick={onOpenDetail}
        className="block w-full text-left px-4 py-3 hover:bg-gray-50/50 transition"
      >
        <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-line">
          {post.content}
        </p>
      </button>

      {/* Image */}
      {image && (
        <button onClick={onOpenDetail} className="block w-full px-4 pb-3">
          <img
            src={image}
            alt=""
            className="w-full rounded-xl object-cover max-h-[400px]"
          />
        </button>
      )}

      {/* Stats */}
      {(likeCount > 0 || commentCount > 0 || repostCount > 0) && (
        <div className="px-4 pb-2 flex items-center gap-4 text-xs text-gray-400">
          {likeCount > 0 && <span>{likeCount} таалагдсан</span>}
          {commentCount > 0 && (
            <button onClick={onOpenDetail} className="hover:text-gray-600">
              {commentCount} сэтгэгдэл
            </button>
          )}
          {repostCount > 0 && <span>{repostCount} хуваалцсан</span>}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center border-t border-gray-100 mx-4">
        <button
          onClick={handleLike}
          className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-sm font-medium transition ${
            liked ? 'text-red-500' : 'text-gray-500 hover:text-red-500'
          }`}
        >
          <Heart className={`size-[18px] ${liked ? 'fill-current' : ''}`} />
          Таалагдсан
        </button>
        <button
          onClick={onOpenDetail}
          className="flex-1 flex items-center justify-center gap-1.5 py-3 text-sm font-medium text-gray-500 hover:text-primary-500 transition"
        >
          <MessageCircle className="size-[18px]" />
          Сэтгэгдэл
        </button>
        <button
          onClick={handleRepost}
          className="flex-1 flex items-center justify-center gap-1.5 py-3 text-sm font-medium text-gray-500 hover:text-green-500 transition"
        >
          <Repeat2 className="size-[18px]" />
          Хуваалцах
        </button>
        <button
          onClick={handleBookmark}
          className={`flex items-center justify-center py-3 px-3 transition ${
            bookmarked ? 'text-primary-500' : 'text-gray-500 hover:text-primary-500'
          }`}
        >
          <Bookmark className={`size-[18px] ${bookmarked ? 'fill-current' : ''}`} />
        </button>
      </div>
    </div>
  )
}

/* ── Post Detail Modal ──────────────────── */
function PostDetailModal({
  post,
  currentUserId,
  currentUser,
  onClose,
  onCommentsUpdate,
}: {
  post: any
  currentUserId?: string
  currentUser?: any
  onClose: () => void
  onCommentsUpdate: (postId: string, comments: any[]) => void
}) {
  const [liked, setLiked] = useState(
    Array.isArray(post.likes) && currentUserId
      ? post.likes.some((id: any) => (id?._id || id)?.toString() === currentUserId)
      : false
  )
  const [likeCount, setLikeCount] = useState(
    Array.isArray(post.likes) ? post.likes.length : 0
  )
  const [bookmarked, setBookmarked] = useState(
    Array.isArray(post.bookmarks) && currentUserId
      ? post.bookmarks.some((id: any) => (id?._id || id)?.toString() === currentUserId)
      : false
  )
  const [repostCount, setRepostCount] = useState(
    Array.isArray(post.reposts) ? post.reposts.length : 0
  )
  const [comments, setComments] = useState<any[]>(post.comments || [])
  const [commentText, setCommentText] = useState('')
  const [sending, setSending] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const commentsEndRef = useRef<HTMLDivElement>(null)

  const author = post.userId || {}
  const authorName = formatName(author.fullName) || 'Хэрэглэгч'
  const authorAvatar = resolveImg(author.avatar)
  const image = resolveImg(post.imageUrl)
  const { user } = useAuth();
  // Focus input on open
  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 100)
  }, [])

  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const handleLike = async () => {
    if( !user ) { showLoginModal(); return }
    try {
      const res = await api.posts.toggleLike(post._id)
      // Backend returns { liked: boolean, likes: number }
      setLiked(res.liked)
      setLikeCount(res.likes)
    } catch {
      // 401 (нэвтрээгүй) бол api.ts автомат login modal нээнэ — UI өөрчлөгдөхгүй
    }
  }

  const handleRepost = async () => {
    if( !user ) { showLoginModal(); return}  
    try {
      const res = await api.posts.toggleRepost(post._id)
      setRepostCount((c: number) => (res.reposted ? c + 1 : c - 1))
    } catch {
      // 401 → login modal
    }
  }

  const handleBookmark = async () => {
    if( !user ) { showLoginModal(); return}
    try {
      const res = await api.posts.toggleBookmark(post._id)
      setBookmarked(res.bookmarked)
    } catch {
      // 401 → login modal
    }
  }

  const handleComment = async () => {
    if (!commentText.trim() || sending) return
    setSending(true)
    try {
      const res = await api.posts.addComment(post._id, commentText.trim())
      const updated = Array.isArray(res)
        ? res
        : [
            ...comments,
            {
              text: commentText.trim(),
              userId: { fullName: currentUser?.fullName || 'Та', avatar: currentUser?.avatar } as any,
              createdAt: new Date().toISOString(),
            },
          ]
      setComments(updated)
      onCommentsUpdate(post._id, updated)
      setCommentText('')
      setTimeout(() => commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100)
    } catch {}
    setSending(false)
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className="relative bg-white rounded-3xl w-full max-w-[560px] max-h-[90vh] flex flex-col overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 shrink-0">
          <h3 className="font-semibold text-base text-gray-900">Нийтлэл</h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-gray-100 transition text-gray-500"
          >
            <X className="size-5" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto">
          {/* Author */}
          <div className="flex items-center gap-3 px-5 pt-5 pb-2">
            <Link to={`/teachers/${author._id || 'me'}`} onClick={onClose}>
              <Avatar src={authorAvatar} alt={authorName} size="md" />
            </Link>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <Link
                  to={`/teachers/${author._id || 'me'}`}
                  onClick={onClose}
                  className="font-semibold text-base text-gray-900 hover:text-primary-500 transition"
                >
                  {authorName}
                </Link>
                {post.isMemberOnly && (
                  <span className="text-[11px] font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                    Зөвхөн гишүүдэд
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-400">{timeAgo(post.createdAt)}</p>
            </div>
          </div>

          {/* Content */}
          <div className="px-5 py-3">
            <p className="text-base text-gray-800 leading-relaxed whitespace-pre-line">
              {post.content}
            </p>
          </div>

          {/* Image */}
          {image && (
            <div className="px-5 pb-3">
              <img
                src={image}
                alt=""
                className="w-full rounded-xl object-cover max-h-[400px]"
              />
            </div>
          )}

          {/* Stats + Actions */}
          <div className="px-5 pb-2 flex items-center gap-4 text-sm text-gray-400">
            {likeCount > 0 && <span>{likeCount} таалагдсан</span>}
            <span>{comments.length} сэтгэгдэл</span>
            {repostCount > 0 && <span>{repostCount} хуваалцсан</span>}
          </div>

          <div className="flex items-center border-t border-b border-gray-100 mx-5">
            <button
              onClick={handleLike}
              className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-sm font-medium transition ${
                liked ? 'text-red-500' : 'text-gray-500 hover:text-red-500'
              }`}
            >
              <Heart className={`size-[18px] ${liked ? 'fill-current' : ''}`} />
              Таалагдсан
            </button>
            <button
              onClick={() => inputRef.current?.focus()}
              className="flex-1 flex items-center justify-center gap-1.5 py-3 text-sm font-medium text-gray-500 hover:text-primary-500 transition"
            >
              <MessageCircle className="size-[18px]" />
              Сэтгэгдэл
            </button>
            <button
              onClick={handleRepost}
              className="flex-1 flex items-center justify-center gap-1.5 py-3 text-sm font-medium text-gray-500 hover:text-green-500 transition"
            >
              <Repeat2 className="size-[18px]" />
              Хуваалцах
            </button>
            <button
              onClick={handleBookmark}
              className={`flex items-center justify-center py-3 px-3 transition ${
                bookmarked ? 'text-primary-500' : 'text-gray-500 hover:text-primary-500'
              }`}
            >
              <Bookmark className={`size-[18px] ${bookmarked ? 'fill-current' : ''}`} />
            </button>
          </div>

          {/* Comments */}
          <div className="px-5 pt-4 pb-2">
            {comments.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-4">
                Эхний сэтгэгдлээ бичээрэй!
              </p>
            )}
            {comments.map((c: any, i: number) => (
              <div key={c._id || i} className="flex gap-2.5 mb-3">
                <Link
                  to={`/profile/${c.userId?._id || 'me'}`}
                  onClick={onClose}
                  className="shrink-0 mt-0.5"
                >
                  <Avatar
                    src={resolveImg(c.userId?.avatar)}
                    alt={formatName(c.userId?.fullName) || ''}
                    size="sm"
                  />
                </Link>
                <div className="flex-1 min-w-0">
                  <div className="bg-gray-50 rounded-2xl px-3.5 py-2.5">
                    <Link
                      to={`/profile/${c.userId?._id || 'me'}`}
                      onClick={onClose}
                      className="text-sm font-semibold text-gray-900 hover:text-primary-500 transition"
                    >
                      {formatName(c.userId?.fullName) || 'Хэрэглэгч'}
                    </Link>
                    <p className="text-sm text-gray-700 mt-0.5">{c.text}</p>
                  </div>
                  <p className="text-xs text-gray-400 mt-1 ml-1">
                    {c.createdAt ? timeAgo(c.createdAt) : ''}
                  </p>
                </div>
              </div>
            ))}
            <div ref={commentsEndRef} />
          </div>
        </div>

        {/* Comment input — fixed at bottom */}
        <div className="shrink-0 border-t border-gray-100 px-5 py-3 bg-white">
          {currentUser ? (
            <div className="flex gap-2.5 items-center">
              <Avatar
                src={resolveImg(currentUser.avatar)}
                alt={formatName(currentUser.fullName) || ''}
                size="sm"
                className="shrink-0"
              />
              <input
                ref={inputRef}
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleComment()}
                placeholder="Сэтгэгдэл бичих..."
                className="flex-1 bg-gray-50 rounded-full px-4 py-2.5 text-sm outline-none text-gray-900 placeholder:text-gray-400 border border-gray-200 focus:border-primary-500 transition"
              />
              <button
                onClick={handleComment}
                disabled={!commentText.trim() || sending}
                className="bg-primary-500 text-white rounded-full p-2.5 hover:bg-primary-600 transition disabled:opacity-50 shrink-0"
              >
                <Send className="size-4" />
              </button>
            </div>
          ) : (
            <Link
              to="/login"
              onClick={onClose}
              className="text-sm text-primary-500 hover:text-primary-600 font-medium"
            >
              Сэтгэгдэл бичихийн тулд нэвтэрнэ үү
            </Link>
          )}
        </div>
      </div>
    </div>
  )
}
